/** What a QR scan landed on, recovered from the URL path alone. */
export interface QrLanding {
  entityType: 'event' | 'community' | null;
  entitySlug: string | null;
}

/** Path prefixes a QR link can target, mapped to the `entity_type` the show side emits. */
const QR_LANDING_PREFIXES: readonly (readonly [string, 'event' | 'community'])[] = [
  ['/events/', 'event'],
  ['/communities/', 'community'],
];

/**
 * Path segments are percent-encoded on the way in (`encodeSegment`, src/utils/slugHelpers.ts)
 * while the show side reads `entity_slug` straight off the model, so decoding here is what
 * keeps the two sides' values comparable rather than a cosmetic tidy-up.
 */
const decodeSegment = (segment: string): string => {
  try {
    return decodeURIComponent(segment);
  } catch {
    // Malformed escape (a lone `%`): the raw segment is still more useful than nothing.
    return segment;
  }
};

/**
 * Split a QR landing pathname into the entity it points at, so `qr_code_scanned`
 * can be joined to `qr_code_shown` on `(source/qr_source, entity_slug)`.
 *
 * Adds nothing to the wire: the slug is already inside the `landing_path` the scan
 * event carries, this only makes it queryable. The join has to work at entity grain
 * because the QR link deliberately carries no `ref_user`: a printed code must not
 * encode a specific person forever.
 *
 * Deliberately separate from `getContextTypeFromPathname`: that returns a different
 * union, runs on the base payload of every event, and has no `/communities/` branch.
 *
 * The segment is whatever the sharer's link used, slug or UUID (see `buildEntitySegment`),
 * so this reports it as found rather than pretending to resolve it. Resolving an id
 * would mean a network call inside AnalyticsBootstrapper.
 */
export const parseQrLanding = (pathname: string): QrLanding => {
  for (const [prefix, entityType] of QR_LANDING_PREFIXES) {
    if (!pathname.startsWith(prefix)) continue;
    // First segment only: `/events/demo-night/photos` is still the demo-night event.
    const segment = pathname.slice(prefix.length).split('/')[0];
    return { entityType, entitySlug: segment === '' ? null : decodeSegment(segment) };
  }
  return { entityType: null, entitySlug: null };
};
